import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import { MetaAdsPanel } from "../components/MetaAdsPanel";
import {
  fetchLeads,
  updateLead,
  leadDate,
  LEAD_PRODUCTS,
  type Lead,
  type LeadStatus,
  type LeadProduct,
} from "../services/leadsService";
import { matchesLeadQuery } from "../services/leadPhone";
import { trackCrmLeadOpened, trackCrmOriginCardViewed } from "../../../analytics/crmAnalytics";
import { CHANNELS, resolveChannel } from "../services/leadSources";
import { leadChannelKey } from "../services/leadAttributionStats";
import { convertLeadToContact } from "../services/contactsService";
import { fetchCategories, categoryClasses } from "../services/pipelinesService";
import {
  trackLeadStatusChanged,
  trackLeadStatusChangeFailed,
  trackLeadConverted,
  trackLeadConversionFailed,
} from "../../../analytics/crmAnalytics";
import ScheduleDemoModal from "../components/ScheduleDemoModal";
import { useTheme } from "../../../context/ThemeContext";
import { FilterSelect, stageFilterOptions } from "../components/FilterSelect";
import { OwnerSelect, useOwnerAgents } from "../components/OwnerSelect";

// ===========================================================================
// Leads — LIVE section. Unlike the mock CRM scaffolds, this reads real leads
// from the API: every marketing-landing contact-form submission lands here as
// a `new` lead. Console users can triage status, inspect details and delete.
// ===========================================================================

const STATUS_META: Record<LeadStatus, { label: string; tone: string }> = {
  new: { label: "Nuevo", tone: "bg-sky-500/15 text-sky-500" },
  contacted: { label: "Contactado", tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  qualified: { label: "Calificado", tone: "bg-primary-500/15 text-primary-500" },
  unqualified: { label: "No calificado", tone: "bg-gray-400/15 text-gray-500" },
  converted: { label: "Convertido", tone: "bg-emerald-500/15 text-emerald-500" },
  lost: { label: "Perdido", tone: "bg-red-500/15 text-red-500" },
};

const STATUS_ORDER: LeadStatus[] = [
  "new",
  "contacted",
  "qualified",
  "unqualified",
  "converted",
  "lost",
];

// Origin badge — distinguishes where a lead came from. Meta Ads leads are
// imported by the webhook with source "meta_ads"; landing-form leads use
// "landing". Anything else falls through to a neutral badge.
const SOURCE_META: Record<string, { label: string; icon: string; tone: string }> = {
  landing: { label: "Landing", icon: "ri-global-line", tone: "bg-primary-500/15 text-primary-500" },
  meta_ads: { label: "Meta Ads", icon: "ri-meta-fill", tone: "bg-blue-500/15 text-blue-600 dark:text-blue-400" },
  import: { label: "Importación", icon: "ri-file-excel-2-line", tone: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
};

function sourceMeta(source: string | null | undefined) {
  const key = (source || "").toLowerCase();
  return (
    SOURCE_META[key] || {
      label: source || "—",
      icon: "ri-bookmark-line",
      tone: "bg-gray-400/15 text-gray-500",
    }
  );
}

function SourceBadge({ source }: { source: string | null | undefined }) {
  const s = sourceMeta(source);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${s.tone}`}>
      <i className={s.icon} />
      {s.label}
    </span>
  );
}

// Canal de captación (#1521): por dónde LLEGÓ la persona (QR, folleto, anuncio,
// chat…). Los leads anteriores a la atribución lo heredan de su `source`
// (misma regla que Fuentes: services/leadAttributionStats).
const leadChannel = (l: Lead): string => leadChannelKey(l);

function ChannelBadge({ channel }: { channel: string | null | undefined }) {
  const c = resolveChannel(channel);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${c.tone}`}>
      <i className={c.icon} />
      {c.label}
    </span>
  );
}

function fmtIso(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Tarjeta «Origen» del detalle: canal, campaña, ref, referrer, ruta y primer toque. */
function AttributionCard({ lead, tc }: { lead: Lead; tc: ReturnType<typeof buildThemeClasses> }) {
  const a = lead.attribution ?? null;
  // PostHog (#1539): una vez por lead mostrado; sin datos de contacto.
  useEffect(() => {
    trackCrmOriginCardViewed({ id: lead.id, product: lead.product, channel: leadChannel(lead), attribution: lead.attribution ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);
  return (
    <div className={`sm:col-span-2 rounded-xl border p-3 ${tc.border}`} data-testid="lead-attribution">
      <p className={`mb-2 text-xs font-semibold ${tc.textSecondary}`}>Origen</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <p className={`text-xs ${tc.textSecondary}`}>Canal</p>
          <div className="mt-1"><ChannelBadge channel={leadChannel(lead)} /></div>
        </div>
        <Detail label="Campaña" value={a?.utm_campaign} tc={tc} />
        <Detail label="Ref / anuncio" value={a?.ref} tc={tc} />
        <Detail label="Medio / fuente" value={[a?.utm_source, a?.utm_medium].filter(Boolean).join(" · ") || null} tc={tc} />
        <Detail label="Contenido / término" value={[a?.utm_content, a?.utm_term].filter(Boolean).join(" · ") || null} tc={tc} />
        <Detail label="Primer toque" value={a?.firstSeenAt ? fmtIso(a.firstSeenAt) : null} tc={tc} />
        <Detail label="Ruta de aterrizaje" value={a?.landingPath} tc={tc} className="sm:col-span-3" />
        <div className="sm:col-span-3">
          <p className={`text-xs ${tc.textSecondary}`}>Referrer</p>
          <p className={`break-all text-sm ${tc.text}`}>{a?.referrer || "—"}</p>
        </div>
      </div>
      {!a && (
        <p className={`mt-2 text-xs ${tc.textSecondary}`}>
          Sin datos de campaña: el canal se deduce de la fuente del lead.
        </p>
      )}
    </div>
  );
}

// Visual style per product. The list endpoint normalizes every lead's product
// to one of these canonical values (backfilling older leads), so rows match by
// exact key.
const PRODUCT_META: Record<string, { label: string; tone: string }> = {
  "Coding Cloud": { label: "Coding Cloud", tone: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  Wayak: { label: "Wayak", tone: "bg-indigo-500/15 text-indigo-500" },
  Nuvex: { label: "Nuvex", tone: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  Movik: { label: "Movik", tone: "bg-orange-500/15 text-orange-600 dark:text-orange-400" },
};

function productLabel(value: Lead["product"]): string {
  if (!value) return "—";
  return PRODUCT_META[String(value)]?.label ?? String(value);
}

/** Trimmed text of a free-form segment field; tolerates a stray non-string value in Firestore. */
function segmentText(v: unknown): string {
  return typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim();
}

/** Distinct, trimmed, alphabetically-sorted non-empty values for a segment select. */
function distinctValues(values: unknown[]): string[] {
  const set = new Set<string>();
  for (const v of values) {
    const t = segmentText(v);
    if (t) set.add(t);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
}

function fmtDate(value: Lead["createdAt"]): string {
  const d = leadDate(value);
  if (!d) return "—";
  return d.toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function LeadsLive() {
  // CC-CONSOLE modals still take isDarkMode; WAYAK styles with tokens elsewhere.
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "all">("all");
  const [productFilter, setProductFilter] = useState<LeadProduct | "all">("all");
  // Qualification-enrichment segments (issue #220): service of interest and the
  // attribution source ("how/where did you hear about us"). Both are free-text,
  // so the options are derived from the distinct values present in the leads.
  const [serviceFilter, setServiceFilter] = useState<string>("all");
  const [sourceAttrFilter, setSourceAttrFilter] = useState<string>("all");
  // Atribución (#1521): canal de captación y campaña (utm_campaign), ambos
  // filtrados en el cliente como los segmentos de arriba.
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [campaignFilter, setCampaignFilter] = useState<string>("all");
  // Responsable: "all", "none" (sin responsable) o el id de un vendedor.
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const owners = useOwnerAgents();
  const [selected, setSelected] = useState<Lead | null>(null);
  // PostHog (#1539): se abrió el detalle de un lead.
  useEffect(() => {
    if (selected) trackCrmLeadOpened({ id: selected.id, product: selected.product, channel: leadChannel(selected), source: selected.source, attribution: selected.attribution ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);
  const [demoLead, setDemoLead] = useState<Lead | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [showMeta, setShowMeta] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await fetchLeads({ limit: 200 });
      setLeads(page.data);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudieron cargar los leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Status label + color come from the pipeline's system categories, so the
  // inbox badges/dropdown stay in lock-step with the board tags (recoloring a
  // category in Settings recolors here too). Falls back to STATUS_META until the
  // categories load — or if the endpoint isn't reachable.
  const [statusMeta, setStatusMeta] = useState<Record<string, { label: string; tone: string }>>(STATUS_META);
  useEffect(() => {
    fetchCategories()
      .then((cats) => {
        const next: Record<string, { label: string; tone: string }> = { ...STATUS_META };
        for (const c of cats) {
          if (c.statusKey && c.statusKey in next) {
            next[c.statusKey] = { label: c.name, tone: categoryClasses(c.color).tone };
          }
        }
        setStatusMeta(next);
      })
      .catch(() => {
        /* keep the static fallback */
      });
  }, []);

  // Status counts respect the active product filter so the dropdown stays meaningful.
  const counts = useMemo(() => {
    const scoped =
      productFilter === "all"
        ? leads
        : leads.filter((l) => l.product === productFilter);
    const c: Record<string, number> = { all: scoped.length };
    for (const s of STATUS_ORDER) c[s] = 0;
    for (const l of scoped) c[l.status] = (c[l.status] ?? 0) + 1;
    return c;
  }, [leads, productFilter]);

  const productCounts = useMemo(() => {
    const c: Record<string, number> = { all: leads.length };
    for (const p of LEAD_PRODUCTS) c[p] = 0;
    for (const l of leads) {
      const p = String(l.product);
      if (p in c) c[p] += 1;
    }
    return c;
  }, [leads]);

  // Distinct, sorted option lists for the free-text qualification segments.
  const serviceOptions = useMemo(
    () => distinctValues(leads.map((l) => l.interestService)),
    [leads],
  );
  const sourceAttrOptions = useMemo(
    () => distinctValues(leads.map((l) => l.acquisitionSource)),
    [leads],
  );
  // Canales presentes (en el orden del catálogo) con su conteo, y campañas.
  const channelOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const l of leads) {
      const k = leadChannel(l);
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return CHANNELS.filter((c) => counts.has(c.key)).map((c) => ({ value: c.key, label: c.label, count: counts.get(c.key) ?? 0 }));
  }, [leads]);
  const campaignOptions = useMemo(
    () => distinctValues(leads.map((l) => l.attribution?.utm_campaign)),
    [leads],
  );

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (productFilter !== "all" && l.product !== productFilter) return false;
      if (serviceFilter !== "all" && segmentText(l.interestService) !== serviceFilter) return false;
      if (sourceAttrFilter !== "all" && segmentText(l.acquisitionSource) !== sourceAttrFilter) return false;
      if (channelFilter !== "all" && leadChannel(l) !== channelFilter) return false;
      if (campaignFilter !== "all" && segmentText(l.attribution?.utm_campaign) !== campaignFilter) return false;
      if (ownerFilter === "none" ? Boolean(l.ownerId) : ownerFilter !== "all" && l.ownerId !== ownerFilter) return false;
      // Free text — includes the plain `phone`, not just `whatsapp` (a landing
      // lead that unchecked "este número tiene WhatsApp" only has `phone`).
      return matchesLeadQuery(l, query);
    });
  }, [leads, query, statusFilter, productFilter, serviceFilter, sourceAttrFilter, channelFilter, campaignFilter, ownerFilter]);

  const changeStatus = async (lead: Lead, status: LeadStatus) => {
    setSavingId(lead.id);
    // Optimistic update
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    setSelected((prev) => (prev && prev.id === lead.id ? { ...prev, status } : prev));
    try {
      await updateLead(lead.id, { status });
      // `lead_source` cierra el circulo con marketing: que canal trae leads que
      // de verdad avanzan. Ni nombre, ni correo, ni WhatsApp, ni empresa.
      trackLeadStatusChanged({
        fromStatus: lead.status ?? "unknown",
        toStatus: status,
        source: lead.source ?? "unknown",
        product: lead.product ?? "unknown",
      });
    } catch {
      trackLeadStatusChangeFailed(status);
      await load(); // revert by reloading on failure
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo actualizar el estado", confirmButtonColor: brand.primary });
    } finally {
      setSavingId(null);
    }
  };

  const changeOwner = async (lead: Lead, ownerId: string | null) => {
    setSavingId(lead.id);
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ownerId } : l)));
    setSelected((prev) => (prev && prev.id === lead.id ? { ...prev, ownerId } : prev));
    try {
      await updateLead(lead.id, { ownerId });
    } catch (e) {
      await load(); // revert by reloading on failure
      const err = e as { response?: { data?: { message?: string } } };
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "No se pudo cambiar el responsable",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setSavingId(null);
    }
  };

  // Leads are never hard-deleted from the Bandeja — "removing" one converts it
  // into a contact: the person is preserved in Contactos and leaves the inbox,
  // the Pipeline board and the call lists.
  const convertToContact = async (lead: Lead) => {
    const res = await Swal.fire({
      icon: "question",
      title: "Convertir en contacto",
      text: `${lead.name ?? "Este lead"} se moverá a Contactos y saldrá de la bandeja, el pipeline y las llamadas.`,
      showCancelButton: true,
      confirmButtonText: "Convertir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: brand.primary,
    });
    if (!res.isConfirmed) return;
    setLeads((prev) => prev.filter((l) => l.id !== lead.id));
    if (selected?.id === lead.id) setSelected(null);
    try {
      await convertLeadToContact(lead.id);
      trackLeadConverted({
        source: lead.source ?? "unknown",
        product: lead.product ?? "unknown",
        score: lead.score ?? 0,
      });
    } catch {
      trackLeadConversionFailed();
      await load();
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo convertir el lead", confirmButtonColor: brand.primary });
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-user-add-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Leads</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Prospectos capturados desde la landing y otros orígenes
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar leads..."
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            <button
              onClick={() => setShowMeta(true)}
              className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:border-primary-500 ${tc.border} ${tc.cardBg} ${tc.text}`}
              title="Conectar Meta Ads"
            >
              <i className="ri-meta-fill text-blue-500" />
              <span className="whitespace-nowrap">Meta Ads</span>
            </button>
            <button
              onClick={load}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
            >
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
              <span className="whitespace-nowrap">Actualizar</span>
            </button>
          </div>
        </div>

        {/* Filtros — desplegables: producto, estado y los segmentos de
            calificación (#220), estos últimos sólo si algún lead trae el dato. */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <FilterSelect
            label="Producto"
            value={productFilter}
            onChange={(v) => setProductFilter(v as LeadProduct | "all")}
            allCount={productCounts.all}
            options={LEAD_PRODUCTS.map((p) => ({ value: p, label: PRODUCT_META[p].label, count: productCounts[p] ?? 0 }))}
            isDarkMode={isDarkMode}
          />
          <FilterSelect
            label="Estado"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as LeadStatus | "all")}
            allCount={counts.all}
            options={stageFilterOptions(STATUS_ORDER, statusMeta, counts)}
            isDarkMode={isDarkMode}
          />
          <FilterSelect
            label="Responsable"
            value={ownerFilter}
            onChange={setOwnerFilter}
            options={[
              { value: "none", label: "Sin responsable", count: leads.filter((l) => !l.ownerId).length },
              ...owners.assignable.map((a) => ({
                value: a.id,
                label: a.name,
                count: leads.filter((l) => l.ownerId === a.id).length,
              })),
            ]}
            isDarkMode={isDarkMode}
          />
          {serviceOptions.length > 0 && (
            <FilterSelect
              label="Servicio de interés"
              value={serviceFilter}
              onChange={setServiceFilter}
              options={serviceOptions.map((s) => ({ value: s, label: s }))}
              isDarkMode={isDarkMode}
            />
          )}
          {sourceAttrOptions.length > 0 && (
            <FilterSelect
              label="Cómo nos conoce"
              value={sourceAttrFilter}
              onChange={setSourceAttrFilter}
              allLabel="Todas"
              options={sourceAttrOptions.map((s) => ({ value: s, label: s }))}
              isDarkMode={isDarkMode}
            />
          )}
          {/* Atribución (#1521): canal de captación y campaña. */}
          {channelOptions.length > 0 && (
            <FilterSelect
              label="Canal"
              value={channelFilter}
              onChange={setChannelFilter}
              allCount={leads.length}
              options={channelOptions}
              isDarkMode={isDarkMode}
            />
          )}
          {campaignOptions.length > 0 && (
            <FilterSelect
              label="Campaña"
              value={campaignFilter}
              onChange={setCampaignFilter}
              allLabel="Todas"
              options={campaignOptions.map((s) => ({ value: s, label: s }))}
              isDarkMode={isDarkMode}
            />
          )}
        </div>

        {/* Table */}
        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          {loading ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" />
              Cargando leads...
            </div>
          ) : error ? (
            <div className="p-10 text-center">
              <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-inbox-line mb-2 block text-3xl" />
              No hay leads que coincidan. Los envíos del formulario de la landing aparecerán aquí.
            </div>
          ) : (
            <div className="w-full overflow-x-auto p-4">
              <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                <thead className="bg-primary-500 text-on-primary">
                  <tr>
                    <th className="p-3 text-left font-bold">Nombre</th>
                    <th className="p-3 text-left font-bold">Producto</th>
                    <th className="p-3 text-left font-bold">Empresa</th>
                    <th className="p-3 text-left font-bold">Contacto</th>
                    <th className="p-3 text-left font-bold">Sistema de interés</th>
                    <th className="p-3 text-left font-bold">Servicio</th>
                    <th className="p-3 text-left font-bold">Cómo nos conoce</th>
                    <th className="p-3 text-left font-bold">Origen</th>
                    <th className="p-3 text-left font-bold">Recibido</th>
                    <th className="p-3 text-center font-bold">Responsable</th>
                    <th className="p-3 text-center font-bold">Estado</th>
                    <th className="p-3 text-center font-bold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l, i) => (
                    <tr
                      key={l.id}
                      className={`text-content ${i % 2 === 0 ? "bg-surface" : "bg-card"}`}
                    >
                      <td className="p-2 font-medium">{l.name ?? "—"}</td>
                      <td className="p-2">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                            PRODUCT_META[String(l.product)]?.tone ?? "bg-gray-400/15 text-gray-500"
                          }`}
                        >
                          {productLabel(l.product)}
                        </span>
                      </td>
                      <td className="p-2">{l.company ?? "—"}</td>
                      <td className="p-2">
                        <div className="flex flex-col">
                          {l.email && <span className="truncate">{l.email}</span>}
                          {l.whatsapp && (
                            <span className={tc.textSecondary}>
                              <i className="ri-whatsapp-line mr-1 text-emerald-500" />
                              {l.whatsapp}
                            </span>
                          )}
                          {/* Phone-only lead (the landing form's WhatsApp box was
                              unchecked). It's the number the AI call dials, so it
                              must be visible here too. */}
                          {l.phone && l.phone !== l.whatsapp && (
                            <span className={tc.textSecondary}>
                              <i className="ri-phone-line mr-1" />
                              {l.phone}
                            </span>
                          )}
                          {!l.email && !l.whatsapp && !l.phone && "—"}
                        </div>
                      </td>
                      <td className="p-2">{l.system ?? "—"}</td>
                      <td className="p-2">{l.interestService ?? "—"}</td>
                      <td className="p-2">
                        {l.acquisitionSource ? (
                          <span>
                            {l.acquisitionSource}
                            {l.referralName ? (
                              <span className={tc.textSecondary}> · {l.referralName}</span>
                            ) : null}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="p-2"><SourceBadge source={l.source} /></td>
                      <td className="p-2 whitespace-nowrap">{fmtDate(l.createdAt)}</td>
                      <td className="p-2 text-center">
                        <OwnerSelect
                          value={l.ownerId}
                          agents={owners.assignable}
                          nameOf={owners.nameOf}
                          disabled={savingId === l.id}
                          onChange={(ownerId) => changeOwner(l, ownerId)}
                          isDarkMode={isDarkMode}
                        />
                      </td>
                      <td className="p-2 text-center">
                        <select
                          value={l.status}
                          disabled={savingId === l.id}
                          onChange={(e) => changeStatus(l, e.target.value as LeadStatus)}
                          className="rounded-md border px-2 py-1 text-xs outline-none border-line bg-app text-content"
                        >
                          {STATUS_ORDER.map((s) => (
                            <option key={s} value={s}>{statusMeta[s].label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            title="Ver detalle"
                            onClick={() => setSelected(l)}
                            className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
                          >
                            <i className="ri-eye-line" />
                          </button>
                          <button
                            title="Agendar demo"
                            onClick={() => setDemoLead(l)}
                            className="rounded-md p-1.5 text-emerald-500 hover:bg-emerald-500/10"
                          >
                            <i className="ri-calendar-schedule-line" />
                          </button>
                          <button
                            title="Convertir en contacto"
                            onClick={() => convertToContact(l)}
                            className="rounded-md p-1.5 text-amber-500 hover:bg-amber-500/10"
                          >
                            <i className="ri-user-shared-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Datos en vivo. Los leads provienen del formulario de la landing (origen “landing”) y de tus
          campañas de <b>Meta Ads</b> (origen “meta_ads”) vía webhook. Usa “Meta Ads” para conectar o
          importar manualmente.
        </p>
      </div>

      {/* Meta Ads integration panel */}
      {showMeta && <MetaAdsPanel onClose={() => setShowMeta(false)} onImported={load} />}

      {/* Schedule a demo from this lead */}
      {demoLead && (
        <ScheduleDemoModal
          lead={demoLead}
          isDarkMode={isDarkMode}
          onClose={() => setDemoLead(null)}
          onScheduled={() => changeStatus(demoLead, "qualified")}
        />
      )}

      {/* Detail modal */}
      {selected && (
        <Modal
          title={`Lead — ${selected.name ?? "—"}`}
          onClose={() => setSelected(null)}
          themeClasses={tc}
          content={
            <div className="space-y-4">
              <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${statusMeta[selected.status].tone}`}>
                {statusMeta[selected.status].label}
              </span>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Detail label="Empresa" value={selected.company} tc={tc} />
                <Detail label="Tamaño de empresa" value={selected.companySize} tc={tc} />
                <Detail label="Correo" value={selected.email} tc={tc} />
                <Detail label="WhatsApp" value={selected.whatsapp} tc={tc} />
                <Detail label="Teléfono" value={selected.phone} tc={tc} />
                <Detail label="Cargo" value={selected.role} tc={tc} />
                <Detail label="Método de contacto" value={selected.contactMethod} tc={tc} />
                <Detail label="Sistema de interés" value={selected.system} tc={tc} className="sm:col-span-2" />
                <Detail label="Servicio de interés" value={selected.interestService} tc={tc} />
                <Detail label="Cómo nos conoce" value={selected.acquisitionSource} tc={tc} />
                <Detail label="Referido por" value={selected.referralName} tc={tc} className="sm:col-span-2" />
                {/* Origen (#1521): canal, campaña y enlace por el que llegó. */}
                <AttributionCard lead={selected} tc={tc} />
                <Detail label="Problema principal" value={selected.problem} tc={tc} className="sm:col-span-2" />
                <Detail label="Producto" value={productLabel(selected.product)} tc={tc} />
                <div>
                  <p className={`text-xs ${tc.textSecondary}`}>Fuente</p>
                  <div className="mt-1"><SourceBadge source={selected.source} /></div>
                </div>
                <Detail label="Recibido" value={fmtDate(selected.createdAt)} tc={tc} />
              </div>

              <div className={`border-t pt-3 ${tc.border}`}>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Responsable</label>
                <OwnerSelect
                  value={selected.ownerId}
                  agents={owners.assignable}
                  nameOf={owners.nameOf}
                  disabled={savingId === selected.id}
                  onChange={(ownerId) => changeOwner(selected, ownerId)}
                  isDarkMode={isDarkMode}
                  className="mb-3 w-full !px-3 !py-2 !text-sm"
                />
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Cambiar estado</label>
                <select
                  value={selected.status}
                  onChange={(e) => changeStatus(selected, e.target.value as LeadStatus)}
                  className="w-full rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content"
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>{statusMeta[s].label}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <button
                  onClick={() => {
                    setDemoLead(selected);
                    setSelected(null);
                  }}
                  className="rounded-lg border border-emerald-500 px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-500/10"
                >
                  <i className="ri-calendar-schedule-line mr-1" /> Agendar demo
                </button>
                {selected.whatsapp && (
                  <a
                    href={`https://wa.me/${selected.whatsapp.replace(/[^\d]/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg border border-emerald-500 px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-500/10"
                  >
                    <i className="ri-whatsapp-line mr-1" /> WhatsApp
                  </a>
                )}
                {!selected.whatsapp && selected.phone && (
                  <a
                    href={`tel:${selected.phone.replace(/[^\d+]/g, "")}`}
                    className="rounded-lg border px-4 py-2 text-sm hover:bg-primary-500/10"
                  >
                    <i className="ri-phone-line mr-1" /> Llamar
                  </a>
                )}
                {selected.email && (
                  <a
                    href={`mailto:${selected.email}`}
                    className="rounded-lg border px-4 py-2 text-sm hover:bg-primary-500/10"
                  >
                    <i className="ri-mail-line mr-1" /> Correo
                  </a>
                )}
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}

function Detail({
  label,
  value,
  tc,
  className = "",
}: {
  label: string;
  value: string | null | undefined;
  tc: ReturnType<typeof buildThemeClasses>;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className={`text-xs ${tc.textSecondary}`}>{label}</p>
      <p className={`text-sm ${tc.text}`}>{value || "—"}</p>
    </div>
  );
}
